'use client';

import { memo } from 'react';
import {
  type ContentBlock,
  type TextBlock,
  type ImageBlock,
  type VideoBlock,
  type GalleryBlock,
} from '@/lib/types/page-builder';
import { EmbedFrame } from '../embed-frame';
import { cn } from '@/lib/utils';

// ============================================================================
// TEXT PREVIEW
// ============================================================================

function TextPreview({ block }: { block: TextBlock }) {
  const getVariantStyles = () => {
    switch (block.style.variant) {
      case 'heading1':
        return 'text-2xl md:text-3xl font-bold leading-tight text-foreground';
      case 'heading2':
        return 'text-xl md:text-2xl font-bold text-foreground';
      case 'heading3':
        return 'text-lg md:text-xl font-semibold text-foreground';
      case 'quote':
        return 'text-base md:text-lg italic border-l-4 border-primary/30 pl-4 text-foreground-secondary';
      default:
        return 'text-base md:text-lg leading-relaxed text-foreground-secondary';
    }
  };

  if (!block.content.html && !block.content.plainText) {
    return null;
  }

  return (
    <div
      className={cn(
        getVariantStyles(),
        '[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2',
        '[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2',
        '[&_li]:my-1',
        '[&_strong]:font-semibold',
        '[&_em]:italic',
        '[&_p]:my-2 first:[&_p]:mt-0 last:[&_p]:mb-0'
      )}
      dangerouslySetInnerHTML={{ __html: block.content.html || `<p>${block.content.plainText}</p>` }}
    />
  );
}

// ============================================================================
// IMAGE PREVIEW
// ============================================================================

function ImagePreview({ block }: { block: ImageBlock }) {
  if (!block.content.image) {
    return null;
  }

  return (
    <figure>
      <img
        src={block.content.image.originalUrl}
        alt={block.content.alt || ''}
        className="w-full h-auto"
      />
      {block.content.caption && (
        <figcaption className="mt-2 text-sm text-center text-foreground-tertiary">
          {block.content.caption}
        </figcaption>
      )}
    </figure>
  );
}

// ============================================================================
// VIDEO PREVIEW
// ============================================================================

const VideoPreview = memo(
  function VideoPreview({ block }: { block: VideoBlock }) {
    const sizing = {
      scalePercent: block.content.scalePercent,
      offsetX: block.content.offsetX,
      offsetY: block.content.offsetY,
    };

    // Custom embed — stored ready to render
    if (block.content.embedSrc) {
      return (
        <EmbedFrame
          src={block.content.embedSrc}
          aspectRatio={block.content.aspectRatio}
          title={block.content.caption || 'Video'}
          {...sizing}
        />
      );
    }

    // Standard YouTube/Vimeo embed
    if (!block.content.embedId) {
      return null;
    }

    const embedUrl =
      block.content.provider === 'youtube'
        ? `https://www.youtube.com/embed/${block.content.embedId}?rel=0`
        : `https://player.vimeo.com/video/${block.content.embedId}`;

    return (
      <EmbedFrame src={embedUrl} title={block.content.caption || 'Video'} {...sizing} />
    );
  },
  (prev, next) =>
    prev.block.content.embedSrc === next.block.content.embedSrc &&
    prev.block.content.embedId === next.block.content.embedId &&
    prev.block.content.provider === next.block.content.provider &&
    prev.block.content.caption === next.block.content.caption &&
    prev.block.content.aspectRatio === next.block.content.aspectRatio &&
    prev.block.content.scalePercent === next.block.content.scalePercent &&
    prev.block.content.offsetX === next.block.content.offsetX &&
    prev.block.content.offsetY === next.block.content.offsetY
);

// ============================================================================
// GALLERY PREVIEW
// ============================================================================

function GalleryPreview({ block }: { block: GalleryBlock }) {
  const count = block.content.images.length;
  if (count === 0) {
    return null;
  }

  const cols = count <= 2 ? 'grid-cols-2' : count === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4';

  return (
    <div className={cn('grid gap-0', cols)}>
      {block.content.images.map((image, index) => (
        <img
          key={image.key || index}
          src={image.originalUrl}
          alt=""
          className="w-full h-auto"
        />
      ))}
    </div>
  );
}

// ============================================================================
// BLOCK PREVIEW RENDERER
// ============================================================================

export function BlockPreview({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'text':
      return <TextPreview block={block} />;
    case 'image':
      return <ImagePreview block={block} />;
    case 'video':
      return <VideoPreview block={block} />;
    case 'gallery':
      return <GalleryPreview block={block} />;
    default:
      return null;
  }
}
